export const navLinks = [{
  label: 'Product',
  href: '#how'
}, {
  label: 'How it works',
  href: '#scenarios'
}, {
  label: 'Methodology',
  href: '/methodology'
}, {
  label: 'Compliance',
  href: '/compliance'
}, {
  label: 'Company',
  href: '/company'
}];
export const heroCommitments = [{
  value: '60–90 min',
  label: 'Real work, not a trivia test'
}, {
  value: 'Six scenarios',
  label: 'Designed across four evidence conditions'
}, {
  value: 'Frontier-model tested',
  label: 'Re-tested against the latest frontier models'
}, {
  value: 'Human decision',
  label: 'No automated hiring decision'
}];

export const roleGroups = [{
  label: 'Finance & investment',
  roles: ['Financial Controller', 'FP&A', 'Investment / Credit Analyst']
}, {
  label: 'Commercial & operating',
  roles: ['Growth', 'Strategy', 'Operations', 'Product', 'Marketing']
}, {
  label: 'Professional & regulated',
  roles: ['Consulting', 'Legal', 'Compliance / Risk', 'Research / Policy']
}];
export const forkStart = 'One candidate, one brief, an hour of work';
export const forkPaths = [{
  title: 'A take-home today',
  tone: 'dead' as const,
  nodes: ['Frontier AI can be given the entire task and evidence', 'Polished deliverable, little visibility into how the candidate reached it', 'Weak hiring signal']
}, {
  title: 'The same hour with Lentic',
  tone: 'live' as const,
  nodes: ['Candidate decides what evidence to share with the AI, and when', 'Six scenarios reveal what they verify, challenge and ultimately rely on', 'Evidence-backed hiring signal']
}];
export const comparisonRows = [{
  axis: 'Premise',
  detection: "Assumes AI isn't used",
  lentic: 'Assumes AI, and supplies it'
}, {
  axis: 'Durability',
  detection: 'Breaks on every model release',
  lentic: 'Re-tested on every model release'
}, {
  axis: 'Failure mode',
  detection: 'False positives, and the exposure they carry',
  lentic: "An inconclusive result, which we report as inconclusive"
}, {
  axis: 'Evidence',
  detection: 'Black-box similarity score',
  lentic: 'Every judgement traces to one action'
}, {
  axis: "When it's wrong",
  detection: "A candidate is rejected for something they didn't do",
  lentic: "A candidate's record shows nothing conclusive, and you interview them"
}, {
  axis: 'Posture',
  detection: 'Surveillance',
  lentic: 'Assessment'
}];
export const atsBlocks = [{
  title: 'Structural conflict',
  body: 'The system making the hiring decision cannot independently audit the decision it produced.'
}, {
  title: 'Evidentiary independence',
  body: 'Lentic creates a versioned record outside the hiring workflow showing what happened, what was checked and what was ultimately decided.'
}, {
  title: 'The audit burden is moving outward',
  body: "As AI becomes embedded in hiring, employers need evidence they can defend, not simply a vendor's assertion that its system worked correctly."
}];
export const scenarioClasses = [{
  id: 'error',
  name: 'AI gives the wrong answer confidently',
  count: 'AI-plausible error',
  what: 'The answer looks reasonable, but the underlying evidence contradicts it.',
  reveals: 'Whether the candidate checks the evidence before trusting AI.',
  example: {
    label: 'Example · AI-plausible error',
    body: 'AI concludes that an intercompany balance requires no adjustment. The answer looks reasonable. The supporting schedule, however, contains a reversal that changes the conclusion.',
    action: 'Accepts the AI conclusion without opening the supporting schedule.',
    outcome: 'Missed',
    adverse: true,
    outcomeNote: 'This is the failure the Decision Record reconstructs.',
    inRecord: 'the moment of acceptance, timestamped, with the unopened source named.'
  }
}, {
  id: 'ambiguity',
  name: 'Evidence appears to conflict',
  count: 'Source ambiguity',
  what: 'Two documents appear inconsistent, but there is a legitimate explanation.',
  reveals: 'Whether the candidate investigates the conflict before deciding.',
  example: {
    label: 'Example · Source ambiguity',
    body: 'Two schedules appear to disagree on an intercompany balance. One reflects the current period; the other carries a prior-period treatment that explains the difference.',
    action: 'Checks both schedules, identifies the period difference and resolves the apparent conflict.',
    outcome: 'Handled · Full',
    inRecord: 'both sources opened, the conflict identified, and the resolution recorded.'
  }
}, {
  id: 'multistep',
  name: 'Nothing is wrong on its own',
  count: 'Multi-step reasoning',
  what: 'The answer only becomes clear by connecting information across several documents.',
  reveals: 'Whether the candidate can connect evidence and reason independently.',
  example: {
    label: 'Example · Multi-step reasoning',
    body: 'No individual document contains the answer. The candidate has to connect the ledger, supporting schedule and transaction history to establish what actually happened.',
    action: 'Connects the evidence across all three sources before reaching a conclusion.',
    outcome: 'Handled · Full',
    inRecord: 'the evidence chain showing which sources were used to reach the conclusion.'
  }
}, {
  id: 'clean',
  name: 'The evidence is correct',
  count: 'Clean case',
  clean: true,
  what: 'AI gives the right answer and the evidence supports it.',
  reveals: 'Whether the candidate verifies appropriately without inventing a problem.',
  example: {
    label: 'Example · Clean case',
    body: 'The AI conclusion is correct and the supporting evidence confirms it. There is no hidden problem to find.',
    action: 'Checks the relevant source once, confirms the conclusion and moves on.',
    outcome: 'Verified proportionately · Full',
    closing: "A candidate who queried the source three times hunting for a problem that didn't exist would score partial. Knowing when to stop is part of the test.",
    inRecord: 'the evidence checked, the conclusion accepted, and no unnecessary escalation.'
  }
}];
export const sessionSteps = [{
  time: '3 min',
  title: 'Consent',
  body: 'Full disclosure before anything is recorded.'
}, {
  time: '5 min',
  title: 'Briefing',
  body: 'A realistic role-based task and the source files. Use the AI as much as you want.'
}, {
  time: '45–75 min',
  title: 'Working session',
  body: 'Complete the task. Decisions and evidence transitions are recorded.'
}, {
  time: '10 min',
  title: 'Reflection',
  body: 'What did you check? What did you trust? What would you do differently?'
}];
export type TraceKind = 'prompt' | 'ai' | 'file' | 'defect' | 'flag' | 'clean';
export const traceEvents: {
  time: string;
  kind: TraceKind;
  label: string;
  detail: string;
  evidence: string;
  weight: 'high' | 'low';
}[] = [{
  time: '00:04:20',
  kind: 'file',
  label: 'Opened brief.pdf',
  detail: 'Read the full brief before prompting, with a second pass over the constraints in §2.',
  evidence: 'dwell 3m 41s · 2 passes',
  weight: 'low'
}, {
  time: '00:12:04',
  kind: 'prompt',
  label: 'Prompt · reconcile intercompany balances',
  detail: 'Task handed over whole rather than staged. No source figures supplied with the request, so the assistant had nothing to reconcile against.',
  evidence: 'prompt 1 of 14 · 62 words',
  weight: 'low'
}, {
  time: '00:14:31',
  kind: 'ai',
  label: 'AI conclusion accepted unedited',
  detail: '"Intercompany balances agree; no adjustment required." Pasted into the deliverable with no edit and no source consulted. Reconstructed in full in the decision record.',
  evidence: '0 edits · no source consulted',
  weight: 'high'
}, {
  time: '00:39:58',
  kind: 'file',
  label: 'Opened brief.pdf · returned to §4',
  detail: 'Went back to the stated constraints after an answer looked too clean. First verification-led action of the session.',
  evidence: 'return visit · §4',
  weight: 'low'
}, {
  time: '00:45:50',
  kind: 'defect',
  label: 'Source ambiguity resolved',
  detail: 'Divergent values across two supporting documents identified, and the unstated authority hierarchy escalated in the deliverable rather than resolved by guessing.',
  evidence: 'scenario 3 of 6 · full credit',
  weight: 'high'
}, {
  time: '00:58:02',
  kind: 'clean',
  label: 'Clean case verified proportionately',
  detail: 'Checked the assistant against one supporting document, found it correct, and moved on without manufacturing a problem. Over-checking is scored here, not rewarded.',
  evidence: 'scenario 4 of 6 · clean · full credit',
  weight: 'high'
}, {
  time: '01:02:16',
  kind: 'flag',
  label: 'AI-plausible error appropriately escalated',
  detail: 'Queried the translation date, rejected the first response, and recorded residual uncertainty. In most roles this is the correct professional behaviour, so it earns near-full credit.',
  evidence: 'scenario 5 of 6 · near-full credit',
  weight: 'high'
}];
export const reconstruction = [{
  field: 'Assistant concluded',
  value: '"Intercompany balances agree; no adjustment required."'
}, {
  field: 'Evidence available',
  value: 'schedule_b.xlsx · brief §4 · AB ledger extract'
}, {
  field: 'Source consulted',
  value: 'None'
}, {
  field: 'Candidate action',
  value: 'Accepted, unedited'
}, {
  field: 'What the source would have established',
  value: 'Counterparty reversed in supporting schedule'
}, {
  field: 'Assistant was',
  value: 'Incorrect'
}, {
  field: 'Outcome',
  value: 'Missed'
}];

/** Recorded at submission, after the work — kept separate from the chronology. */
export const reconstructionAttestation = [{
  field: 'Candidate confidence',
  value: 'High'
}, {
  field: 'Would sign off?',
  value: 'Yes',
  adverse: true
}];
export const pipelineStages = [{
  stage: '01',
  title: 'Construct',
  engine: 'Build',
  body: 'Build the evidence and the intended failure mode.'
}, {
  stage: '02',
  title: 'Test',
  engine: 'Frontier APIs',
  body: 'Run the scenario against current frontier models.'
}, {
  stage: '03',
  title: 'Attack',
  engine: 'Adversarial',
  body: 'Test whether the model can bypass the intended challenge.'
}, {
  stage: '04',
  title: 'Validate',
  engine: 'Practitioner',
  body: 'Human review confirms the scenario remains realistic.'
}];

/** The full production detail, shown on the methodology page only. */
export const pipelineDetail = [{
  stage: 'Stage 1',
  title: 'Synthetic scaffolding',
  engine: 'LLM',
  body: 'Entity, narrative, ledger, budget and supporting documents, generated to a schema.'
}, {
  stage: 'Stage 2',
  title: 'Deterministic construction',
  engine: 'Python',
  body: 'Seeded, reproducible scenario builds that emit a machine-readable ground truth record.'
}, {
  stage: 'Stage 3',
  title: 'Adversarial gate',
  engine: 'Frontier APIs',
  body: 'Submitted cold to current models. Anything resolved unprompted fails and regenerates.'
}, {
  stage: 'Stage 4',
  title: 'Human validation',
  engine: 'Practitioner',
  body: 'Pass/fail review that the surviving scenarios are realistic, not generator artefacts.'
}];
export const gateFacts = [{
  value: 'Weeks',
  label: 'Realistic scenario shelf life, not years'
}, {
  value: 'Auto',
  label: 'Deprecation when a scenario stops holding'
}, {
  value: 'Locked',
  label: 'Library version pinned for the life of a requisition'
}];
export const modelIndependence = [{
  label: 'Scenario construction',
  value: 'No model dependency'
}, {
  label: 'Ground truth records',
  value: 'No model dependency'
}, {
  label: 'Measurement layer',
  value: 'No model dependency'
}, {
  label: 'Record schema and retention',
  value: 'No model dependency'
}];
export const archetypes = [{
  id: 'counterparty_mismatch',
  behaviour: 'Amount reconciles; entity swapped for a plausible sibling.'
}, {
  id: 'silent_restatement',
  behaviour: 'Prior-period comparative altered, with no restatement note.'
}, {
  id: 'source_conflict',
  behaviour: 'Two documents disagree; authority hierarchy left unstated.'
}, {
  id: 'stale_period',
  behaviour: "A document's period shifted a quarter, metadata held consistent."
}, {
  id: 'internal_contradiction',
  behaviour: 'A constraint in brief §2 quietly negated in §6.'
}, {
  id: 'classification_drift',
  behaviour: 'Correct total, adjacent account, altered headline ratio.'
}, {
  id: 'fx_timing',
  behaviour: 'Correct rate, wrong date, on a European entity.'
}];
export const compliancePoints = [{
  tag: 'Independent evidence',
  body: 'Lentic records the assessment independently of the hiring workflow.'
}, {
  tag: 'Human decision',
  body: 'Lentic does not make the employment decision. A named human reviews the evidence and signs off.'
}, {
  tag: 'Data minimisation',
  body: 'We capture the evidence required to reconstruct the assessment, not unnecessary surveillance data.'
}, {
  tag: 'Regulatory readiness',
  body: 'Designed with emerging requirements around AI-assisted employment decisions in mind.'
}];
export const neverCaptured = ['No video', 'No webcam', 'No keystroke logging outside the workspace', 'No personality or trait inference', 'No affect analysis', 'No demographic inference'];
export const candidateFacts = ['Use the AI as much as you like. We measure how, not whether.', 'Full disclosure of what is captured, before the session starts.', '60–90 minutes, asynchronous, no webcam, no proctor.', 'Accommodations on request: extended timing, reduced-telemetry mode, alternative formats.', 'A written account of their own performance, available on request.', 'No pass mark is published to candidates. The record is evidence, not a grade.'];
export const evidenceEstablished = ['The mechanic runs end to end in a live sandbox', 'Scenarios survive cold testing against three frontier models under four prompting conditions', 'Every score decomposes to one action, one timestamp, one scenario'];
export const evidenceInProgress = ['A 20–30 participant study against independently rated professional quality, pre-registered, with results published including failures', 'A three-arm comparison: assistant alone, human with full-access assistant, and Lentic', 'Predictive validity against job outcomes, a 12–18 month question we are not yet claiming'];
export const decisionSummaryException = 'One material verification failure: an incorrect AI conclusion was accepted without opening the supporting schedule, and the candidate recorded that they would have signed off on it.';
export const decisionSummary = 'Calibrated judgement was observed across the other five scenarios. The candidate challenged one plausible AI error, resolved a source ambiguity by escalating the unstated authority question rather than guessing, and accepted both clean cases after proportionate verification.';
export const defectResults = [{
  id: 'Scenario 1',
  type: 'Clean case',
  outcome: 'Verified proportionately' as const,
  detail: 'Single confirming check against the ledger extract, then moved on.',
  credit: 'Full'
}, {
  id: 'Scenario 2',
  type: 'Multi-step relational',
  outcome: 'Handled · resolved correctly' as const,
  detail: 'Chained the budget, ledger and schedule to reach the conclusion unaided by the assistant.',
  credit: 'Full'
}, {
  id: 'Scenario 3',
  type: 'Source ambiguity',
  outcome: 'Handled · resolved correctly' as const,
  detail: 'Both schedules opened at 00:41:12, divergence investigated, authority question escalated.',
  credit: 'Full'
}, {
  id: 'Scenario 4',
  type: 'Clean case',
  outcome: 'Verified proportionately' as const,
  detail: 'Assistant correct and supported by the evidence. Checked once, accepted, no manufactured problem.',
  credit: 'Full'
}, {
  id: 'Scenario 5',
  type: 'AI-plausible error',
  outcome: 'Handled · appropriately escalated' as const,
  detail: 'Translation date queried, first response rejected, the open question escalated in the deliverable.',
  credit: 'Near-full'
}, {
  id: 'Scenario 6',
  type: 'AI-plausible error',
  outcome: 'Missed' as const,
  detail: 'Conclusion accepted from the assistant; the supporting schedule was never opened.',
  credit: 'None',
  reconstructed: true
}];
export const observedPrimary = {
  label: 'Source consulted before relying on AI output',
  value: '7 of 9 material decisions'
};
export const observedSupporting = [{
  label: 'Clean cases appropriately accepted',
  value: '2 of 2'
}, {
  label: 'AI output challenged where required',
  value: '1 of 2 material AI errors'
}, {
  label: 'AI output materially edited or discarded',
  value: '46%'
}, {
  label: 'Prompts, staged with checkpoints',
  value: '14'
}];
export const candidateReflection = 'I checked both schedules on the intercompany question because the totals felt too clean. On the translation date I ran out of time and flagged it rather than guessing. Looking back I should have opened schedule_b before accepting the first reconciliation.';
export const companyIntro = ['Lentic is an independent assessment layer for hiring into AI-assisted work.', 'We put candidates in a sandbox with an assistant that can reason but cannot see the source documents, and we record how they handle six scenarios of known construction.', 'What the client buys is the record that proves how the decision was made, held by a party with no stake in the outcome.'];
export const founders = [{
  name: 'Founder name',
  role: 'Co-founder',
  background: 'Fifteen years in group finance and controls, latterly leading month-end close for a listed European group.',
  linkedin: 'https://www.linkedin.com'
}, {
  name: 'Founder name',
  role: 'Co-founder',
  background: 'Machine learning engineer; previously built evaluation and measurement infrastructure for production model systems.',
  linkedin: 'https://www.linkedin.com'
}];
export const footerColumns = [{
  title: 'Product',
  links: [{
    label: 'How it works',
    href: '#how'
  }, {
    label: 'Session trace',
    href: '/methodology#trace'
  }, {
    label: 'Decision record',
    href: '#record'
  }, {
    label: 'Scenario library',
    href: '/methodology'
  }, {
    label: 'For candidates',
    href: '/compliance#candidates'
  }]
}, {
  title: 'Company',
  links: [{
    label: 'About Lentic',
    href: '/company'
  }, {
    label: 'Evidence and status',
    href: '/company'
  }, {
    label: 'Compliance',
    href: '/compliance'
  }, {
    label: 'Pilot pricing',
    href: '#contact'
  }]
}, {
  title: 'Legal',
  links: [{
    label: 'Privacy policy',
    href: '#legal'
  }, {
    label: 'Candidate privacy notice',
    href: '#legal'
  }, {
    label: 'Data processing agreement',
    href: '#legal'
  }, {
    label: 'Security',
    href: '#legal'
  }]
}];